import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import AdvancedOptions from '@/components/AdvancedOptions';
import copyIcon from '@/components/common/icons/copy.svg';
import OrderIcon from '@/components/OrderIcon/OrderIcon';
import ReportHistory from '@/components/ReportHistory';
import { APP_REPAROFACIL_URL } from '@/config';
import { useUser } from '@/contexts/UserContext';
import { hasFranchiseeActionAccess } from '@/helpers/orderPermissions';
import AccessInformationSection from '@/orderDetails/AccessInformationSection';
import AccompaniedInspectionSection from '@/orderDetails/AccompaniedInspectionSection';
import ActivityLog from '@/orderDetails/ActivityLog';
import AdditionalCostsSection from '@/orderDetails/AdditionalCostsSection';
import AdditionalsSection from '@/orderDetails/AdditionalsSection';
import AddressSection from '@/orderDetails/AddressSection';
import AnnotationSection from '@/orderDetails/AnnotationSection';
import Attachments from '@/orderDetails/Attachments';
import BrySignersSection from '@/orderDetails/BrySignersSection';
import BudgetDuringInspectionSection from '@/orderDetails/BudgetDuringInspectionSection';
import CancelSection from '@/orderDetails/CancelSection';
import ClientSection from '@/orderDetails/ClientSection';
import ConstructionDetailsSection from '@/orderDetails/ConstructionDetailsSection';
import CostSection from '@/orderDetails/CostSection';
import DesiredPropertySection from '@/orderDetails/DesiredPropertySection';
import DetailsSection from '@/orderDetails/DetailsSection';
import DraftScheduleSection from '@/orderDetails/DraftScheduleSection';
import FichaCertaSection from '@/orderDetails/FichaCertaSection';
import FilesToSignSection from '@/orderDetails/FilesToSignSection';
import FranchiseeSection from '@/orderDetails/FranchiseeSection';
import InspectionEditRequestsSection from '@/orderDetails/InspectionEditRequestsSection';
import Inspections from '@/orderDetails/Inspections';
import Inspectors from '@/orderDetails/Inspectors';
import InterestedPersonSection from '@/orderDetails/InterestedPersonSection';
import KeysSection from '@/orderDetails/KeysSection';
import LocationReferencesSection from '@/orderDetails/LocationReferencesSection';
import MediasSection from '@/orderDetails/MediasSection';
import PartiesInvolvedSection from '@/orderDetails/PartiesInvolvedSection';
import PhotoBuildingDetailsSection from '@/orderDetails/PhotoBuildingDetailsSection';
import PotentialScheduleSection from '@/orderDetails/PotentialScheduleSection';
import PreviousInspectionSection from '@/orderDetails/PreviousInspectionSection';
import PropertyAccessSection from '@/orderDetails/PropertyAccessSection';
import PropertyInformationsSection from '@/orderDetails/PropertyInformationsSection';
import RecommendationSection from '@/orderDetails/RecommendationSection';
import ResourcesSection from '@/orderDetails/ResourcesSection';
import SignersSection from '@/orderDetails/SignersSection';
import SolicitationsSection from '@/orderDetails/SolicitationsSection';
import TagSection from '@/orderDetails/TagSection';
import TourAccessInfo from '@/orderDetails/TourAccessInfo';
import VacationNoticeSection from '@/orderDetails/VacationNoticeSection';
import ViewResourcesSection from '@/orderDetails/ViewResourcesSection';
import { showToast } from '@/store/ducks/toasts';
import { Button } from '@/components/ui/button';
import renderSvg from '@/utils/renderSvg';

const OrderDetailsBody = ({
  currentPage,
  data,
  refreshHandler,
  updateOrderItem,
  openEditPreviousInspection,
}) => {
  const intl = useIntl();
  const user = useUser();
  const dispatch = useDispatch();

  const orderFranchisee = data?.franchisee_id;
  const isFromTheSameFranchisee = user?.franchiseeId === orderFranchisee;
  const hasActionAccess = hasFranchiseeActionAccess(user, data);

  const canShowInspectors =
    !data.type?.includes('marketplace') &&
    user?.authRole === 'ROLE_FRANCHISEE' &&
    data.inspectors !== undefined &&
    (['FINISHED', 'CANCELED', 'VNR', 'FINALIZED_BY_DEVICE'].includes(data?.status) || !!data.inspectors?.length);

  const canShowAnnotation = user?.authRole === 'ROLE_FRANCHISEE' && hasActionAccess;
  const canEditPreviousInspection =
    user?.authRole === 'ROLE_FRANCHISEE' &&
    isFromTheSameFranchisee &&
    data?.status === 'ACCEPTED' &&
    data?.is_archived === false &&
    !!data?.form?.previous_inspection;

  const copyToClipboard = async url => {
    await navigator.clipboard.writeText(url);
    dispatch(
      showToast({
        type: 'success',
        title: intl.formatMessage({ id: 'toast.success.title', defaultMessage: 'Sucesso!' }),
        text: intl.formatMessage({ id: 'toast.linkCopied', defaultMessage: 'Link copiado.' }),
        duration: 5000,
      })
    );
  };

  return (
    <div className="grid grid-cols-12 items-center">

      <div className="col-span-12 grid grid-cols-12 min-h-[60vh]">
        <div className="col-span-6">
          <div className="text-[#858585] text-sm font-bold" data-cy="order-details-type">
            <FormattedMessage id={data.form.inspection_type || data.type} />

            {data?.hotsite_url && (
              <span className="font-normal">
                &nbsp; - <FormattedMessage id="orderDetails.openOn" /> &nbsp;
                <a href={data.hotsite_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                  <FormattedMessage id="orderDetails.myImovelDigital" />
                </a>
                &nbsp; &nbsp;
                {renderSvg(copyIcon, {
                  className: 'cursor-pointer inline-block align-middle',
                  alt: intl.formatMessage({ id: 'orderDetails.copyLinkAlt' }),
                  title: intl.formatMessage({ id: 'orderDetails.copyLinkTitle' }),
                  onClick: () => copyToClipboard(data.hotsite_url),
                })}
                &nbsp; &nbsp;
              </span>
            )}
            {data?.type === 'tourVirtual' && (
              <span className="font-normal">
                &nbsp; - <FormattedMessage id="orderDetails.openOn" /> &nbsp;
                <a
                  href={
                    data?.medias?.find(media => media.type === 'TOUR_VIRTUAL')?.external_uri ||
                    'https://gerenciador.banibconecta.com/manager'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  <FormattedMessage id="orderDetails.banibPanel" />
                </a>
                &nbsp; &nbsp;
                {renderSvg(copyIcon, {
                  className: 'cursor-pointer inline-block align-middle',
                  alt: intl.formatMessage({ id: 'orderDetails.copyLinkAlt' }),
                  title: intl.formatMessage({ id: 'orderDetails.copyLinkTitle' }),
                  onClick: () =>
                    copyToClipboard(
                      data?.medias?.find(media => media.type === 'TOUR_VIRTUAL')?.external_uri ||
                        'https://gerenciador.banibconecta.com/manager'
                    ),
                })}
                &nbsp; &nbsp;
              </span>
            )}
            {data?.type === 'marketplace_reparofacil' && (
              <span className="font-normal">
                &nbsp; - &nbsp;
                <Button
                  variant="default"
                  size="sm"
                  className="bg-[#00BBDD] hover:bg-[#0099bb] text-white min-w-[120px] px-8 normal-case"
                  onClick={() =>
                    window.open(
                      `${APP_REPAROFACIL_URL}/budgets/${data?.metadata?.budget_public_id}`,
                      '_blank'
                    )
                  }
                >
                  <FormattedMessage id="go_to_reparofacil" />
                </Button>
              </span>
            )}

            {data?.messages?.cancel ? (
              <FormattedMessage id="orderDetails.canceledSuffix" defaultMessage=" (Pedido cancelado)" />
            ) : null}
          </div>
        </div>

        <div className="col-span-6 text-right leading-none">
          <AdvancedOptions
            orderFranchisee={orderFranchisee}
            orderId={data.id}
            orderType={data.form?.inspection_type || data.type}
            orderStatus={data.status}
            refreshHandler={refreshHandler}
            updateOrderItem={updateOrderItem}
            order={data}
          />
        </div>

        {data?.responsible && (
          <div
            className="col-span-10 text-[#858585] text-sm font-bold"
            data-cy="order-details-responsible"
          >
            <FormattedMessage id="ORDER_RESPONSIBLE" />: {data?.responsible?.name} ({data?.responsible?.email}){' '}
            {data?.responsible?.phone && ` - ${data?.responsible?.phone}`}
          </div>
        )}

        {data.form.tags && (
          <div className="col-span-12">
            <TagSection tags={data.form.tags} />
          </div>
        )}
        {data?.messages?.cancel && (
          <div className="col-span-12">
            <CancelSection order={data} />
          </div>
        )}
        {data.form.clients && (
          <div className="col-span-12">
            <ClientSection creator={data.creator} clients={data.form.clients} />
          </div>
        )}
        {data.form.franchisees && (
          <div className="col-span-12">
            <FranchiseeSection franchisees={data.form.franchisees} />
          </div>
        )}
        {data.form.documents && (
          <div className="col-span-12">
            <FilesToSignSection documents={data.form.documents} />
          </div>
        )}
        {data.form.details && (
          <div className="col-span-12">
            <DetailsSection details={data.form.details} />
          </div>
        )}
        {data.form.additionalCosts?.price && (
          <div className="col-span-12">
            <AdditionalCostsSection additionalCosts={data.form.additionalCosts} />
          </div>
        )}
        {data.form.construction_details && (
          <div className="col-span-12">
            <ConstructionDetailsSection data={data.form.construction_details} />
          </div>
        )}
        {data.form.solicitations && (
          <div className="col-span-12">
            <SolicitationsSection data={data.form.solicitations} />
          </div>
        )}
        {data.form.address && (
          <div className="col-span-12">
            <AddressSection address={data.form.address} />
          </div>
        )}
        {data.form.desired_property && (
          <div className="col-span-12">
            <DesiredPropertySection property={data.form.desired_property} />
          </div>
        )}
        {(data?.resources || []).length > 0 && (
          <div className="col-span-12">
            <ViewResourcesSection resources={data.resources} />
          </div>
        )}
        {data.form.location_references && (
          <div className="col-span-12">
            <LocationReferencesSection locationReferences={data.form.location_references} />
          </div>
        )}
        {data.form.parties_involved && (
          <div className="col-span-12">
            <PartiesInvolvedSection partiesInvolved={data.form.parties_involved} />
          </div>
        )}
        {data.form.keys && (
          <div className="col-span-12">
            <KeysSection keys={data.form.keys} />
          </div>
        )}
        {data.form.cost && (
          <div className="col-span-12">
            <CostSection cost={data.form.cost} />
          </div>
        )}
        {data.form.additionals && (
          <div className="col-span-12">
            <AdditionalsSection additionals={data.form.additionals} />
          </div>
        )}
        {data.form.accessInformation && (
          <div className="col-span-12">
            <AccessInformationSection data={data.form.accessInformation} />
          </div>
        )}
        {data.form.tourAccessInfo && (
          <div className="col-span-12">
            <TourAccessInfo data={data.form.tourAccessInfo} />
          </div>
        )}
        {data.form.budgetDuringInspection && (
          <div className="col-span-12">
            <BudgetDuringInspectionSection data={data.form.budgetDuringInspection} />
          </div>
        )}
        {data.form.accompaniedInspection && (
          <div className="col-span-12">
            <AccompaniedInspectionSection data={data.form.accompaniedInspection} />
          </div>
        )}
        {data.form.vacationNotice?.tenantEmail && (
          <div className="col-span-12">
            <VacationNoticeSection data={data.form.vacationNotice.tenantEmail} />
          </div>
        )}
        {data.form.propertyInformations && (
          <div className="col-span-12">
            <PropertyInformationsSection data={data.form.propertyInformations} />
          </div>
        )}
        {data.form.photoBuildingDetails && (
          <div className="col-span-12">
            <PhotoBuildingDetailsSection data={data.form.photoBuildingDetails} />
          </div>
        )}
        {data.form.propertyAccess && (
          <div className="col-span-12">
            <PropertyAccessSection data={data.form.propertyAccess} />
          </div>
        )}
        {data.form.interestedPerson && (
          <div className="col-span-12">
            <InterestedPersonSection data={data.form.interestedPerson} />
          </div>
        )}
        {data.form.potentialSchedule && (
          <div className="col-span-12">
            <PotentialScheduleSection data={data.form.potentialSchedule} />
          </div>
        )}
        {data.form.recommendation && data.form.recommendation.code && (
          <div className="col-span-12">
            <RecommendationSection data={data.form.recommendation} />
          </div>
        )}
        {data.form.previous_inspection && (
          <div className="col-span-12">
            <PreviousInspectionSection
              previousInspection={data.form.previous_inspection}
              orderId={data.id}
              orderType={data.form.inspection_type || data.type}
              canEditPreviousInspection={canEditPreviousInspection}
              openEditPreviousInspection={openEditPreviousInspection}
            />
          </div>
        )}
        {data.status === 'ACCEPTED' &&
          !data.type?.includes('marketplace_') &&
          user?.authRole === 'ROLE_FRANCHISEE' && (
            <div className="col-span-12">
              <DraftScheduleSection orderId={data.id} />
            </div>
          )}
        {Array.isArray(data.form.signers) &&
          data.form.signers.length > 0 &&
          (data.form.type === 'bry' ? (
            <div className="col-span-12">
              <BrySignersSection
                orderId={data.id}
                refreshHandler={refreshHandler}
                signers={data.form.signers}
                requestId={data.form.id}
                copyToClipboard={copyToClipboard}
              />
            </div>
          ) : (
            <div className="col-span-12">
              <SignersSection signers={data.form.signers} />
            </div>
          ))}
        {data.form.recipients && (
          <div className="col-span-12">
            <SignersSection signers={data.form.recipients} />
          </div>
        )}

        {currentPage === 'active' && data.inspections && (
          <div className="col-span-12">
            <Inspections
              orderId={data.id}
              inspections={data.inspections}
              hasActionAccess={hasActionAccess}
              updateOrderItem={updateOrderItem}
              refreshHandler={refreshHandler}
            />
          </div>
        )}

        {data.status === 'FINALIZED_BY_DEVICE' &&
          user?.authRole === 'ROLE_FRANCHISEE' &&
          hasActionAccess &&
          data.inspections &&
          data.inspections.some(i => i.official === true) && (
            <div className="col-span-12">
              <InspectionEditRequestsSection orderId={data.id} refreshHandler={refreshHandler} />
            </div>
          )}

        {data.form.completed_at && (
          <div className="col-span-12">
            <ResourcesSection data={data} />
          </div>
        )}
        {data.type && data.type.includes('marketplace') ? null : (
          <div className="col-span-12">
            <Attachments
              data={data.form.attachments}
              orderId={data.id}
              canAdd={currentPage === 'sent' || data?.status === 'FINISHED'}
            />
          </div>
        )}
        {!!data?.medias?.length && (
          <div className="col-span-12">
            <MediasSection medias={data.medias} />
          </div>
        )}
        {data.metadata?.fichacerta?.parecer && (
          <div className="col-span-12">
            <FichaCertaSection data={data.metadata.fichacerta.parecer} />
          </div>
        )}
        {data.reports && (
          <div className="col-span-12">
            <ReportHistory data={data.reports} orderId={data.id} orderStatus={data.status} />
          </div>
        )}

        <div className="col-span-12">
          <ActivityLog orderId={data.id} />
        </div>

        {canShowInspectors && (
          <div className="col-span-12">
            <Inspectors
              inspectors={data.inspectors}
              canAdd={['FINISHED', 'VNR', 'FINALIZED_BY_DEVICE'].includes(data?.status)}
              orderId={data.id}
              refreshHandler={refreshHandler}
            />
          </div>
        )}

        {canShowAnnotation && (
          <div className="col-span-12">
            <AnnotationSection
              annotation={data.annotation || {}}
              orderId={data.id}
              orderCode={data.code}
              refreshHandler={refreshHandler}
              inspectionIcon={data.type && <OrderIcon orderType={data.type} fontSize="24px" />}
              orderPage={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

OrderDetailsBody.propTypes = {
  currentPage: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  openEditPreviousInspection: PropTypes.func,
  refreshHandler: PropTypes.func.isRequired,
  updateOrderItem: PropTypes.func,
};

OrderDetailsBody.defaultProps = {
  openEditPreviousInspection: null,
  updateOrderItem: null,
};

export default OrderDetailsBody;
